import { useCallback, useMemo } from 'react';

import type { FeatureGroup } from '@hotjar/webapp/domains/revenue/plan/types';
import { BUSINESS } from '@hotjar/webapp/domains/revenue/plan/feature-group';
import { useProductCatalog } from '@hotjar/webapp/domains/common/product/hooks/useProductCatalog';
import {
  extractSkuFromPlanId,
  getProductsByProductType,
  isBundledPlanId,
} from '@hotjar/webapp/domains/common/product/utils';

import type { PlanComparisonFeatureGateDetails } from '../types';

interface Props {
  initialRecommendedPlanId?: string | null;
  featureGate: PlanComparisonFeatureGateDetails | null;
}

const DEFAULT_RECOMMENDED_FEATURE_GROUP = BUSINESS;

/**
 * Determines which plan to recommend to the user in the plan comparison modal, based on a number of inputs.
 * From highest to lowest priority, these are:
 *
 *    1) null if Plan Agnostic
 *    2) plan ID from the `initialRecommendedPlanId` prop
 *    3) lowest available plan of the feature gate's minimum feature group resolved by the `referrer` prop
 *    4) a default recommendation (lowest Business plan)
 *
 * This hook should always return an SKU (e.g. `analytics_basic_2000`). It should NEVER return a bundled plan ID,
 * (e.g. `analytics_basic_2000;feedback_basic`). If you see that happening, it's broken.
 */
export const usePlanComparisonPlanRecommendation = ({
  initialRecommendedPlanId,
  featureGate,
}: Props): string | null => {
  const { data: productCatalog } = useProductCatalog();
  const productType = featureGate?.sku;
  const products = getProductsByProductType(productType, productCatalog);

  /**
   * Utility function for getting the default (lowest) plan of a feature group.
   */
  const getDefaultPlanForFeatureGroup = useCallback(
    (featureGroup?: FeatureGroup) => {
      return products?.find((product) => product.tier === featureGroup)?.sku;
    },
    [products],
  );

  /**
   * If `initialRecommendedPlanId` is specified, the upsell caller explicitly declared a specific plan to upsell to,
   * which means we shouldn't bother resolving any other recommendation. If specified, this takes precedence over
   * every other kind of recommendation.
   *
   * If `initialRecommendedPlanId` is a bundled plan ID (e.g. `analytics_basic_2000;feedback_basic`), we need to
   * convert it to an SKU (e.g. `analytics_basic_2000` for Observe).
   */
  const basedOnInitialRecommendedPlanId =
    initialRecommendedPlanId && productType && isBundledPlanId(initialRecommendedPlanId)
      ? extractSkuFromPlanId(initialRecommendedPlanId, productType)
      : initialRecommendedPlanId;

  /**
   * If we have a feature gate for this referrer, get the minimum feature group of it, and get the lowest plan
   * of that feature group.
   */
  const featureGatePlan = useMemo(
    () => getDefaultPlanForFeatureGroup(featureGate?.minimumFeatureGroup),
    [featureGate?.minimumFeatureGroup, getDefaultPlanForFeatureGroup],
  );

  /**
   * Unless specified otherwise, we want to recommend the lowest Business plan by default.
   */
  const defaultRecommendedPlanId = useMemo(
    () => getDefaultPlanForFeatureGroup(DEFAULT_RECOMMENDED_FEATURE_GROUP),
    [getDefaultPlanForFeatureGroup],
  );

  // We return null if plan agnostic. By definition, agnosticism means that there
  // is no recommendation
  if (featureGate?.isPlanAgnostic) {
    return null;
  }

  return basedOnInitialRecommendedPlanId ?? featureGatePlan ?? defaultRecommendedPlanId ?? null;
};
